import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import {
  getPhaseProgress,
  getTripPhase,
  normalizeTripStatus,
  phaseLabel,
  TRIP_PHASE_CLOSED,
} from '../../lib/utils';
import { StatusBadge } from './StatusBadge';
import { TRIP_STATUSES } from './tripTimelineShared';

const EMPTY_ARRAY = [];

const stateClasses = {
  completed: {
    dot: 'border-success/60 bg-success text-white',
    card: 'border-success/30 bg-success/10 text-success',
    line: 'bg-success',
    label: 'Terminé',
  },
  active: {
    dot: 'border-flame/60 bg-flame text-white',
    card: 'border-flame/30 bg-flame/10 text-flame-ink shadow-sm',
    line: 'bg-flame',
    label: 'En cours',
  },
  pending: {
    dot: 'border-border bg-card text-muted-foreground/70',
    card: 'border-border bg-card text-muted-foreground',
    line: 'bg-secondary',
    label: 'À venir',
  },
};

const formatHistoryDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const TripTimeline = ({
  currentStatus,
  statusHistory = EMPTY_ARRAY,
  onStatusClick,
  className = '',
  managesDepot = true,
}) => {
  const normalizedStatus = normalizeTripStatus(currentStatus);

  const historyByStatus = statusHistory.reduce((acc, item) => {
    const key = normalizeTripStatus(item.status);
    if (key && !acc[key]) acc[key] = item;
    return acc;
  }, {});

  // Sans dépôt, l'étape « Arrivé au dépôt » n'existe pas — sauf pour un ancien
  // trajet qui y est passé.
  const sansEtapeDepot = managesDepot === false
    && normalizedStatus !== 'ARRIVED_AT_DEPOT'
    && !historyByStatus.ARRIVED_AT_DEPOT;

  const visibleStatuses = TRIP_STATUSES.filter(
    (status) => (status.key !== 'CANCELLED' || normalizedStatus === 'CANCELLED')
      && !(sansEtapeDepot && status.key === 'ARRIVED_AT_DEPOT'),
  );
  const safeCurrentIndex = Math.max(
    0,
    visibleStatuses.findIndex((status) => status.key === normalizedStatus),
  );
  // Le trajet se lit en deux phases: la progression affichée est celle de la
  // phase en cours, sinon une livraison qui démarre s'annonce déjà à 75%.
  const currentPhase = getTripPhase(normalizedStatus);
  const progress = getPhaseProgress(normalizedStatus, { managesDepot: !sansEtapeDepot });

  // On a long journey the current step sits far to the right, so the rail
  // opened showing only finished steps — the one thing the reader came for was
  // off-screen with nothing to say so. `block: 'nearest'` keeps this from
  // dragging the whole page down to the timeline.
  const activeStepRef = useRef(null);
  const railRef = useRef(null);
  const [overflow, setOverflow] = React.useState({ start: false, end: false });

  // A fade pinned to the right edge claims there is more to see even when the
  // rail is already at its end, and says nothing about the steps hidden behind
  // the left edge once it has scrolled. Track both.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const measure = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      setOverflow({ start: rail.scrollLeft > 4, end: rail.scrollLeft < max - 4 });
    };
    activeStepRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
    measure();
    rail.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    return () => {
      rail.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [normalizedStatus]);

  const getState = (index, key) => {
    if (key === 'CANCELLED' && normalizedStatus !== 'CANCELLED') return 'pending';
    if (normalizedStatus === 'CANCELLED') return key === 'CANCELLED' ? 'active' : 'completed';
    // Un trajet terminé n'a plus d'étape « En cours »: l'en-tête disait
    // « Terminé » quand la dernière case affichait « Clôturé — En cours ».
    if (normalizedStatus === 'COMPLETED' && index <= safeCurrentIndex) return 'completed';
    if (index < safeCurrentIndex) return 'completed';
    if (index === safeCurrentIndex) return 'active';
    return 'pending';
  };

  return (
    <section className={`rounded-2xl border border-border bg-card p-4 shadow-sm ${className}`}>
      <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-flame-ink">
            Avancement du trajet
          </p>
          <h2 className="text-xl font-black text-foreground">
            {currentPhase === TRIP_PHASE_CLOSED
              ? 'Parcours opérationnel'
              : `Phase ${phaseLabel(currentPhase).toLowerCase()}`}
          </h2>
        </div>
        <div className="rounded-full border border-border bg-muted/50 px-3 py-1 text-sm font-bold text-muted-foreground">
          {Number.isFinite(progress) ? Math.max(0, Math.min(100, progress)) : 0}% de la phase
        </div>
      </div>

      {/* One scrolling rail at every width.
          Thirteen steps in a six-column grid wrapped onto three rows, and the
          connector — absolutely positioned inside each card at
          w-[calc(100%+10px)] — carried on past the last card of every row and
          out of the panel. The zig-zag (alternate cards pushed down by mt-10)
          only reads as a serpentine when the row is unbroken; wrapped, it was
          just noise, and it cost 450px of height for a chronology.
          Laying the connectors out between the cards instead of inside them
          makes overflow structurally impossible. */}
      <div className="relative">
      {/* tabIndex makes the rail scrollable by keyboard: the steps still to
          come are disabled buttons, so without a focusable container there was
          no way to reach the end of the journey without a mouse. */}
      <ol
        ref={railRef}
        tabIndex={0}
        role="group"
        aria-label="Étapes du trajet"
        className="flex snap-x snap-mandatory gap-0 overflow-x-auto scroll-smooth pb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {visibleStatuses.map((status, index) => {
          const state = getState(index, status.key);
          const Icon = status.icon;
          const historyItem = historyByStatus[status.key];
          const date = formatHistoryDate(historyItem?.created_at);
          const clickable = Boolean(onStatusClick && state !== 'pending');
          const isLast = index === visibleStatuses.length - 1;
          const previous = visibleStatuses[index - 1];
          const startsNewPhase =
            previous &&
            previous.phase !== status.phase &&
            status.phase !== TRIP_PHASE_CLOSED;

          return (
            <li
              key={status.key}
              ref={state === 'active' ? activeStepRef : undefined}
              className="flex shrink-0 snap-start items-start"
            >
              {startsNewPhase && (
                <span className="mr-2 mt-[18px] flex shrink-0 flex-col items-center gap-1">
                  <span aria-hidden="true" className="h-12 w-px bg-border" />
                  <span className="whitespace-nowrap text-[10px] font-black uppercase tracking-wide text-muted-foreground">
                    {phaseLabel(status.phase)}
                  </span>
                </span>
              )}
              <motion.button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onStatusClick(status.key)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index, 8) * 0.03 }}
                aria-current={state === 'active' ? 'step' : undefined}
                className={`w-[152px] rounded-xl border p-3 text-left transition ${stateClasses[state].card} ${
                  clickable ? 'hover:-translate-y-0.5 hover:shadow-sm' : ''
                }`}
              >
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full border-2 ${stateClasses[state].dot}`}
                >
                  <Icon className="size-4" aria-hidden="true" />
                </span>
                <span className="mt-2.5 block text-sm font-semibold leading-tight text-foreground">
                  {status.label}
                </span>
                {status.optional && (
                  <span className="mt-1 block text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    Facultatif
                  </span>
                )}
                {/* bg-white/80 stayed white in dark mode; the token follows the theme. */}
                <span className="mt-1.5 inline-flex rounded-full bg-background/70 px-2 py-0.5 text-[11px] font-semibold">
                  {stateClasses[state].label}
                </span>
                {date && (
                  <span className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="size-3 shrink-0" aria-hidden="true" />
                    {date}
                  </span>
                )}
                {historyItem?.location_text && (
                  <span className="mt-1 block truncate text-xs text-muted-foreground">
                    {historyItem.location_text}
                  </span>
                )}
              </motion.button>

              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`mt-[30px] h-0.5 w-4 shrink-0 rounded-full ${stateClasses[state].line}`}
                />
              )}
            </li>
          );
        })}
      </ol>
        {/* Without these a card just stops at the panel edge and reads as
            clipped rather than scrollable. */}
        {overflow.start && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-card to-transparent"
          />
        )}
        {overflow.end && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-card to-transparent"
          />
        )}
      </div>
    </section>
  );
};

const HorizontalTripProgress = ({ currentStatus, className = '' }) => (
  <TripTimeline currentStatus={currentStatus} className={className} />
);

export default TripTimeline;
export { HorizontalTripProgress, StatusBadge };
